import PropTypes from 'prop-types';
import * as React from 'react';
import { useState } from 'react';
import {
  TextField,
  FormControl,
  MenuItem,
  Stack,
  Divider,
  Box,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CloseIcon from '@mui/icons-material/Close';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { dispatch } from '../../../../redux/store';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { postAddResident } from '../../../../redux/slices/rentals';
import { fNoNegativeOrLetters } from '../../../../utils/formatText';

const filter = createFilterOptions();

ResidentInformation.propTypes = {
  setResident: PropTypes.func,
  resident: PropTypes.object,
  facility: PropTypes.object,
  facilityResidents: PropTypes.array
};

export default function ResidentInformation({ resident, setResident, facility, facilityResidents }) {
  const { enqueueSnackbar } = useSnackbar();

  const [open, toggleOpen] = useState(false);
  const [showSecondaryInsuranceControls, setShowSecondaryInsuranceControls] = useState(false);
  const handleSecondaryInsuranceControlsButtonClick = () => {
    setShowSecondaryInsuranceControls(true);
  };
  const handleClose = () => {
    setDialogValue({
      firstName: '',
      lastName: '',
      gender: '',
      dob: '',
      floor: '',
      unit: '',
      room: '',
      bed: '',
      feet: '',
      inches: '',
      weight: '',
      primaryInsurance: '',
      primaryInsuranceKind: '',
      primaryInsurancePolicyNum: '',
    });
    toggleOpen(false);
  };

  const [dialogValue, setDialogValue] = React.useState({
    firstName: '',
    lastName: '',
    gender: '',
    dob: '',
    floor: '',
    unit: '',
    room: '',
    bed: '',
    feet: '',
    inches: '',
    weight: '',
    primaryInsurance: '',
    primaryInsuranceKind: '',
    primaryInsurancePolicyNum: '',
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const values = {
      firstName: dialogValue.firstName,
      lastName: dialogValue.lastName,
      gender: dialogValue.gender,
      dob: dialogValue.dob,
      floor: dialogValue.floor,
      unit: dialogValue.unit,
      room: dialogValue.room,
      bed: dialogValue.bed,
      height: dialogValue.feet * 12 + parseInt(dialogValue.inches, 10), // Convert feet and inches to total inches
      weight: dialogValue.weight,
      primaryInsurance: dialogValue.primaryInsurance,
      primaryInsuranceKind: dialogValue.primaryInsuranceKind,
      primaryInsurancePolicyNum: dialogValue.primaryInsurancePolicyNum,
      secondaryInsurance: dialogValue.secondaryInsurance || '',
      secondaryInsuranceKind: dialogValue.secondaryInsuranceKind || '',
    };
    const response = await dispatch(postAddResident(values, facility.id));
    if (response) {
      setResident(response);
    } else {
      enqueueSnackbar(`Failed to create resident`, { variant: 'error' });
    }
    handleClose();
  };

  const isDobToday = dialogValue.dob ? new Date(dialogValue.dob).toDateString() === new Date().toDateString() : false;

  return (
    <>
      <DialogTitle
        sx={{
          textAlign: 'center',
          color: 'rgba(112, 112, 112, 0.5)',
        }}
      >
        Resident Information
      </DialogTitle>
      <DialogContentText sx={{ fontWeight: 'bold' }}>Resident Name</DialogContentText>{' '}
      <Autocomplete
        sx={{
          width: '100%',
          mt: 1,
          '& legend': { display: 'none' },
          '& fieldset': { top: 0 },
          '& .MuiFormLabel-root ': 'none',
        }}
        value={resident || null}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            // timeout to avoid instant validation of the dialog's form.
            setTimeout(() => {
              toggleOpen(true);
              setDialogValue({
                firstName: newValue,
                lastName: '',
                gender: '',
                dob: '',
                floor: '',
                unit: '',
                room: '',
                bed: '',
                feet: '',
                inches: '',
                weight: '',
                primaryInsurance: '',
                primaryInsuranceKind: '',
                primaryInsurancePolicyNum: '',
              });
            });
          } else if (newValue && newValue.inputValue) {
            toggleOpen(true);
            setDialogValue({
              firstName: newValue.inputValue,
              lastName: '',
              gender: '',
              dob: '',
              floor: '',
              unit: '',
              room: '',
              bed: '',
              feet: '',
              inches: '',
              weight: '',
              primaryInsurance: '',
              primaryInsuranceKind: '',
              primaryInsurancePolicyNum: '',
            });
          } else {
            setResident(newValue);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (params.inputValue !== '') {
            filtered.push({
              inputValue: params.inputValue,
              firstName: `Add "${params.inputValue}"`,
              lastName: '',
              dob: new Date(''),
              gender: '',
              floor: '',
              unit: '',
              room: '',
              bed: '',
              feet: '',
              inches: '',
              weight: 0,
              primaryInsurance: '',
              primaryInsuranceKind: '',
              primaryInsurancePolicyNumber: '',
            });
          }

          return filtered;
        }}
        id="resident-search-dialog"
        options={[...facilityResidents].sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        })}
        getOptionLabel={(option) => `${option?.firstName} ${option?.lastName}`}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        renderOption={(props, option) => (
          <li
            {...props}
            style={{ color: option?.inputValue ? 'purple' : 'inherit' }}
          >{`${option?.firstName} ${option?.lastName}`}</li>
        )}
        freeSolo
        forcePopupIcon
        renderInput={(params) => <TextField
          {...params}
          title="Resident Name"
          placeholder = 'Please type to search or add resident'
          autoComplete="off"
        />}
      />
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: '#8cc53f',
            fontSize: '24px',
            fontWeight: 'bold',
            marginTop: 2,
            mb: -2,
          }}
        >
          New Rental
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              textAlign: 'center',
              color: 'rgba(112, 112, 112, 0.5)',
              fontWeight: 'medium',
            }}
          >
            New Resident
          </DialogTitle>
          <DialogContent>
            <Box>
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <FormControl fullWidth>
                      <TextField
                        label="First Name"
                        required
                        size="small"
                        autoFocus
                        type="text"
                        autoComplete="off"
                        value={dialogValue.firstName}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            firstName: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                          mt: 1,
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth>
                      <TextField
                        autoComplete="off"
                        label="Last Name"
                        required
                        size="small"
                        type="text"
                        value={dialogValue.lastName}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            lastName: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                          mt: 1,
                        }}
                      />
                    </FormControl>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <FormControl>
                      <TextField
                        label="Gender"
                        autoComplete="off"
                        size="small"
                        fullWidth
                        select
                        value={dialogValue.gender}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            gender: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                          minWidth: '222px',
                          maxWidth: '222px',
                          ml: 4,
                        }}
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                      </TextField>
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth>
                      <Box
                        sx={{
                          width: '100%',
                          minWidth: '222px',
                          maxWidth: '222px',
                          mr: 4,
                        }}
                      >
                        <DatePicker
                          disableFuture
                          required
                          slotProps={{ textField: { size: 'small', inputProps: { readonly: 'true' } } }}
                          label="Date of Birth*"
                          value={dialogValue.dob ? new Date(dialogValue.dob) : null}
                          onChange={(date) =>
                            setDialogValue({
                              ...dialogValue,
                              dob: date ? new Date(date.getFullYear(), date.getMonth(), date.getDate()) : null,
                            })
                          }
                          sx={{
                            width: '100%',
                          }}
                        />
                        {isDobToday && (
                          <Box sx={{ color: 'red', mt: 1, fontSize: '0.9rem' }}>
                            Please enter an accurate date of birth.
                          </Box>
                        )}
                      </Box>
                    </FormControl>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <FormControl id="feet">
                      <TextField
                        label="Feet"
                        size="small"
                        type="number"
                        autoComplete="off"
                        value={dialogValue.feet}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            feet: event.target.value,
                          })
                        }
                        sx={{
                          width: '103px',
                        }}
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => fNoNegativeOrLetters(e)}
                      />
                    </FormControl>
                    <FormControl id="inches" sx={{ ml: 2 }}>
                      <TextField
                        label="Inches"
                        size="small"
                        type="number"
                        autoComplete="off"
                        value={dialogValue.inches}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            inches: event.target.value,
                          })
                        }
                        sx={{
                          width: '103px',
                        }}
                        inputProps={{ min: 0, max: 11 }}
                        onKeyDown={(e) => fNoNegativeOrLetters(e)}
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth id="weight">
                      <TextField
                        label="Weight"
                        size="small"
                        type="text"
                        autoComplete="off"
                        value={dialogValue.weight}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            weight: parseFloat(event.target.value),
                          })
                        }
                        sx={{
                          width: '100%',
                        }}
                        onKeyDown={(e) => fNoNegativeOrLetters(e)}
                      />
                    </FormControl>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <Box>
                      <Divider sx={{ width: '475px', borderColor: '#4ca5e7', borderStyle: 'dashed' }} />
                    </Box>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <FormControl fullWidth id="floor">
                      <TextField
                        label="Floor"
                        size="small"
                        type="text"
                        value={dialogValue.floor}
                        autoComplete="off"
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            floor: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth id="unit">
                      <TextField
                        label="Unit"
                        size="small"
                        type="text"
                        value={dialogValue.unit}
                        autoComplete="off"
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            unit: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                        }}
                      />
                    </FormControl>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <FormControl fullWidth id="room">
                      <TextField
                        label="Room"
                        size="small"
                        type="text"
                        value={dialogValue.room}
                        autoComplete="off"
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            room: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth id="bed">
                      <TextField
                        label="Bed"
                        size="small"
                        type="text"
                        value={dialogValue.bed}
                        autoComplete="off"
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            bed: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                        }}
                      />
                    </FormControl>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <Box>
                      <Divider sx={{ width: '475px', borderColor: '#4ca5e7', borderStyle: 'dashed' }} />
                    </Box>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Box>
                    <FormControl fullWidth id="primaryInsurance">
                      <TextField
                        label="Primary Insurance"
                        size="small"
                        type="text"
                        autoComplete="off"
                        value={dialogValue.primaryInsurance}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            primaryInsurance: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                        }}
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl fullWidth id="primaryInsuranceKind">
                      <TextField
                        label="Primary Insurance Kind"
                        size="small"
                        type="text"
                        autoComplete="off"
                        value={dialogValue.primaryInsuranceKind}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            primaryInsuranceKind: event.target.value,
                          })
                        }
                        sx={{
                          width: '100%',
                        }}
                      />
                    </FormControl>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="center" alignItems="center">
                  <Box>
                    <Box>
                      <FormControl fullWidth id="primaryInsurancePolicyNumber">
                        <TextField
                          label="Primary Insurance Policy Number"
                          size="small"
                          type="text"
                          autoComplete="off"
                          value={dialogValue.primaryInsurancePolicyNum}
                          onChange={(event) =>
                            setDialogValue({
                              ...dialogValue,
                              primaryInsurancePolicyNum: event.target.value,
                            })
                          }
                          sx={{
                            width: '100%',
                            mr: 30,
                          }}
                        />
                      </FormControl>
                    </Box>
                  </Box>
                </Stack>
                {showSecondaryInsuranceControls && (
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                    <Box>
                      <Box>
                        <Divider sx={{ width: '475px', borderColor: '#4ca5e7', borderStyle: 'dashed' }} />
                      </Box>
                    </Box>
                  </Stack>
                )}
                {!showSecondaryInsuranceControls && (
                  <Stack direction="row" justifyContent="center" alignItems="center">
                    <Box>
                      <FormControl fullWidth id="secondaryInsuranceButton">
                        <Button
                          onClick={handleSecondaryInsuranceControlsButtonClick}
                          variant="outlined"
                          color="secondary"
                          sx={{ justifyContent: 'center', width: '100%', mr: 5, ml: 9 }}
                        >
                          + Add Secondary Insurance
                        </Button>
                      </FormControl>
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                      }}
                    />
                  </Stack>
                )}
                {showSecondaryInsuranceControls && (
                  <>
                    {' '}
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                      <Box>
                        <FormControl fullWidth id="secondaryInsurance">
                          <TextField
                            label="Secondary Insurance"
                            size="small"
                            type="text"
                            autoComplete="off"
                            value={dialogValue.secondaryInsurance}
                            onChange={(event) =>
                              setDialogValue({
                                ...dialogValue,
                                secondaryInsurance: event.target.value,
                              })
                            }
                            sx={{
                              width: '100%',
                            }}
                          />
                        </FormControl>
                      </Box>
                      <Box>
                        <FormControl fullWidth id="secondaryInsuranceKind">
                          <TextField
                            label="Secondary Insurance Kind"
                            size="small"
                            type="text"
                            autoComplete="off"
                            value={dialogValue.secondaryInsuranceKind}
                            onChange={(event) =>
                              setDialogValue({
                                ...dialogValue,
                                secondaryInsuranceKind: event.target.value,
                              })
                            }
                            sx={{
                              width: '100%',
                            }}
                          />
                        </FormControl>
                      </Box>
                    </Stack>
                    <Stack direction="row" justifyContent="center" alignItems="center">
                      <Box>
                        <FormControl fullWidth id="secondaryInsurancePolicyNumber">
                          <TextField
                            label="Secondary Insurance Policy Number"
                            size="small"
                            type="text"
                            autoComplete="off"
                            value={dialogValue.secondaryInsurancePolicyNumber}
                            onChange={(event) =>
                              setDialogValue({
                                ...dialogValue,
                                secondaryInsurancePolicyNumber: event.target.value,
                              })
                            }
                            sx={{
                              width: '100%',
                              mr: 30,
                            }}
                          />
                        </FormControl>
                      </Box>
                    </Stack>{' '}
                  </>
                )}
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
            {' '}
            <Button
              type="submit"
              variant="contained"
              sx={{ display: 'flex', justifyContent: 'center', width: '60%', mt: 2, mb: 2 }}
              disabled={
                dialogValue.firstName === '' || dialogValue.lastName === '' || dialogValue.dob === '' || isDobToday
              }
            >
              Next
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
